import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* ------------------------------------------------------------------------
 * Independence Day welcome popup.
 *
 * Splashes when the site opens on 15 August, holds long enough for the wish
 * to be read, then fades out on its own. Four ways out, identical in effect:
 * the button, the ✕, the scrim, Escape.
 *
 * The scene raises itself — pole, then flag climbing, unfurling, and finally
 * a shower of marigolds off its lower edge. Nothing and nobody hoists it.
 *
 * Every static rule is inline here; styles/independence-day-popup.css holds
 * the keyframes and nothing else.
 *
 * The overlay is align-items:flex-start with margin:auto on the card, and
 * deliberately not align-items:center — a centred flex item that overflows a
 * fixed container puts its close button out of reach on short screens.
 * ---------------------------------------------------------------------- */

const SESSION_KEY = "w4t-id-popup-seen";
const FADE_MS = 520;

/* ---- the sky: 14 sparkles, [x, y, scale, duration(s), delay(s)] ---- */
const STARS = [
    [30, 44, 1.15, 2.6, 0], [94, 30, 0.7, 3.0, 0.55], [268, 56, 1.1, 2.7, 0.2],
    [186, 132, 0.7, 3.15, 1.9], [72, 96, 0.85, 3.1, 0.35], [230, 116, 0.82, 3.3, 1.05],
    [44, 162, 1.0, 2.8, 0.8], [280, 172, 0.95, 2.9, 0.7], [22, 244, 0.8, 3.4, 1.2],
    [244, 250, 0.75, 3.5, 1.45], [58, 318, 0.9, 2.9, 1.6], [266, 330, 0.88, 3.0, 2.2],
    [122, 354, 0.74, 2.85, 1.5], [212, 458, 0.72, 2.9, 2.1],
];

const PETAL = "M0 -6 Q1 -1 6 0 Q1 1 0 6 Q-1 1 -6 0 Q-1 -1 0 -6 Z";

/* ---- the shower: released along the flag's lower edge ---- */
const FLOWERS = [
    { left: 6, top: 56, size: 13, anim: "a", dur: 3.4, delay: 4.25, petals: 6, petal: "#FF9933", centre: "#E3A21F" },
    { left: 24, top: 68, size: 9, anim: "c", dur: 3.9, delay: 4.5, petals: 4, petal: "#FFF3E0", centre: "#F5C86B" },
    { left: 40, top: 60, size: 11, anim: "b", dur: 3.2, delay: 4.35, petals: 6, petal: "#F2A03D", centre: "#D98B14" },
    { left: 56, top: 72, leaf: true, anim: "a", dur: 4.1, delay: 4.95, petal: "#7BB661" },
    { left: 68, top: 58, size: 12, anim: "c", dur: 3.6, delay: 4.6, petals: 6, petal: "#FFB347", centre: "#E3A21F" },
    { left: 86, top: 66, size: 10, anim: "b", dur: 3.8, delay: 4.8, petals: 4, petal: "#FFF3E0", centre: "#F5C86B" },
    { left: 102, top: 54, size: 13, anim: "a", dur: 3.5, delay: 4.4, petals: 6, petal: "#FF9933", centre: "#E3A21F" },
    { left: 14, top: 74, size: 9, anim: "b", dur: 4.2, delay: 5.3, petals: 4, petal: "#F2A03D", centre: "#D98B14" },
    { left: 50, top: 76, size: 11, anim: "c", dur: 4.0, delay: 5.15, petals: 6, petal: "#FFB347", centre: "#E3A21F" },
    { left: 78, top: 78, leaf: true, anim: "b", dur: 4.4, delay: 5.5, petal: "#8FC474" },
    { left: 96, top: 72, size: 10, anim: "a", dur: 4.3, delay: 5.6, petals: 4, petal: "#FFF3E0", centre: "#F5C86B" },
    { left: 34, top: 52, size: 12, anim: "c", dur: 3.7, delay: 4.7, petals: 6, petal: "#FF9933", centre: "#E3A21F" },
];

/* ---- tricolour pieces falling down the scrim, behind the card ---- */
const CONFETTI = [
    { left: "12%", w: 8, h: 8, radius: "2px", colour: "#FF7722", dur: 9, delay: 0.2 },
    { left: "26%", w: 7, h: 10, radius: "0", colour: "#ffffff", dur: 11, delay: 1.1 },
    { left: "38%", w: 8, h: 8, radius: "50%", colour: "#138808", dur: 10, delay: 2.3 },
    { left: "61%", w: 7, h: 7, radius: "2px", colour: "#FF7722", dur: 12, delay: 0.8 },
    { left: "74%", w: 8, h: 11, radius: "0", colour: "#ffffff", dur: 10.5, delay: 3.4 },
    { left: "88%", w: 7, h: 7, radius: "50%", colour: "#138808", dur: 13, delay: 1.9 },
];

const BAND_COLOURS = ["#FF7722", "#F4F6F4", "#138808"];
const BAND_DELAYS = [".15s", ".28s", ".41s"];

/** The tricolour strip. Head and foot render from the same function, so the
 *  two are identical by construction rather than by keeping two blocks in
 *  step by hand. */
function Strip() {
    return (
        <div style={{ display: "flex", height: 8 }} aria-hidden="true">
            {BAND_COLOURS.map((colour, i) => (
                <span
                    key={colour}
                    style={{
                        flex: 1,
                        background: colour,
                        transformOrigin: "left center",
                        animation: `id-band .7s cubic-bezier(.2,.8,.2,1) ${BAND_DELAYS[i]} both`,
                    }}
                />
            ))}
        </div>
    );
}

/** Marigold. Six petals, or four with the diagonal pair dropped. */
function Marigold({ size, petals, petal, centre }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            aria-hidden="true"
            style={{ display: "block" }}
        >
            <g fill={petal}>
                <ellipse cx="12" cy="5" rx="3.6" ry="5" />
                <ellipse cx="12" cy="19" rx="3.6" ry="5" />
                <ellipse cx="5" cy="12" rx="5" ry="3.6" />
                <ellipse cx="19" cy="12" rx="5" ry="3.6" />
                {petals === 6 && (
                    <>
                        <ellipse cx="7.2" cy="7.2" rx="4.4" ry="3.2" transform="rotate(-45 7.2 7.2)" />
                        <ellipse cx="16.8" cy="16.8" rx="4.4" ry="3.2" transform="rotate(-45 16.8 16.8)" />
                    </>
                )}
            </g>
            <circle cx="12" cy="12" r={petals === 6 ? 3.1 : 3} fill={centre} />
        </svg>
    );
}

/** The flag itself: three equal bands, 24 spokes drawn as 12 diameters. */
function FlagSvg() {
    return (
        <svg
            viewBox="0 0 900 600"
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ display: "block", width: "100%", height: "100%" }}
        >
            <rect width="900" height="200" fill="#FF9933" />
            <rect y="200" width="900" height="200" fill="#FFFFFF" />
            <rect y="400" width="900" height="200" fill="#138808" />
            <g transform="translate(450 300)">
                <circle r="75" fill="none" stroke="#000080" strokeWidth="8" />
                <circle r="15" fill="#000080" />
                <g stroke="#000080" strokeWidth="4.5" strokeLinecap="round">
                    {Array.from({ length: 12 }, (_, k) => (
                        <line key={k} x1="0" y1="-75" x2="0" y2="75" transform={`rotate(${k * 15})`} />
                    ))}
                </g>
            </g>
        </svg>
    );
}

export default function IndependenceDayPopup({
    autoCloseSeconds = 9,
    oncePerSession = false,
    onClose,
}) {
    const [open, setOpen] = useState(() => {
        if (typeof window === "undefined") return false;
        if (!oncePerSession) return true;
        try {
            return window.sessionStorage.getItem(SESSION_KEY) !== "1";
        } catch (e) {
            return true; // storage blocked — just show it
        }
    });
    const [fading, setFading] = useState(false);
    const [hover, setHover] = useState(false);

    const closeRef = useRef(null);
    const restoreRef = useRef(null);
    const fadeTimer = useRef(null);
    const autoTimer = useRef(null);

    const dismiss = useCallback(() => {
        setFading((already) => {
            if (already) return already;
            fadeTimer.current = window.setTimeout(() => {
                setOpen(false);
                if (onClose) onClose();
            }, FADE_MS);
            return true;
        });
    }, [onClose]);

    /* Auto-close. The extra 0.9s covers the card entrance, so the visitor
     * gets the full reading time rather than losing the first second of it. */
    useEffect(() => {
        if (!open) return undefined;

        if (oncePerSession) {
            try {
                window.sessionStorage.setItem(SESSION_KEY, "1");
            } catch (e) {
                /* storage blocked — the popup simply shows again next visit */
            }
        }

        autoTimer.current = window.setTimeout(dismiss, (autoCloseSeconds + 0.9) * 1000);

        return () => {
            window.clearTimeout(autoTimer.current);
            window.clearTimeout(fadeTimer.current);
        };
    }, [open, autoCloseSeconds, oncePerSession, dismiss]);

    /* Escape to leave; focus parked on the ✕ and handed back on close; the
     * page behind held still while the card is up. */
    useEffect(() => {
        if (!open) return undefined;

        restoreRef.current = document.activeElement;

        const onKeyDown = (event) => {
            if (event.key === "Escape") dismiss();
        };
        document.addEventListener("keydown", onKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        if (closeRef.current) closeRef.current.focus({ preventScroll: true });

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
            const restore = restoreRef.current;
            if (restore && typeof restore.focus === "function") {
                restore.focus({ preventScroll: true });
            }
        };
    }, [open, dismiss]);

    if (!open || typeof document === "undefined") return null;

    return createPortal(
        <div
            className="id-popup"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 60,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                overflowY: "auto",
                padding: "var(--id-overlay-pad, 24px)",
                opacity: fading ? 0 : 1,
                pointerEvents: fading ? "none" : "auto",
                transition: "opacity .5s ease",
            }}
            onClick={(event) => {
                if (event.target === event.currentTarget) dismiss();
            }}
        >
            {/* scrim — clicking it closes */}
            <div
                onClick={dismiss}
                style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(26,58,143,.42)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    animation: "id-scrim .5s ease both",
                }}
            />

            {/* confetti, falling down the scrim behind the card */}
            <div
                aria-hidden="true"
                style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none" }}
            >
                {CONFETTI.map((c, i) => (
                    <span
                        key={i}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: c.left,
                            width: c.w,
                            height: c.h,
                            borderRadius: c.radius,
                            background: c.colour,
                            opacity: 0,
                            animation: `id-pop-confetti ${c.dur}s linear ${c.delay}s infinite`,
                        }}
                    />
                ))}
            </div>

            <div
                role="dialog"
                aria-modal="true"
                aria-label="Happy Independence Day from WAY4TRACK"
                style={{
                    position: "relative",
                    zIndex: 1,
                    margin: "auto",
                    width: "min(920px, 100%)",
                    borderRadius: "var(--id-card-radius, 26px)",
                    background: "#fff",
                    overflow: "hidden",
                    boxShadow: "0 30px 80px rgba(16,32,74,.34)",
                    animation: "id-card-in .85s cubic-bezier(.2,.9,.25,1.03) both",
                }}
            >
                <Strip />

                <button
                    type="button"
                    ref={closeRef}
                    onClick={dismiss}
                    aria-label="Close"
                    style={{
                        position: "absolute",
                        top: 22,
                        right: 20,
                        zIndex: 3,
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        borderRadius: "50%",
                        border: "1px solid rgba(26,58,143,.14)",
                        background: "rgba(255,255,255,.92)",
                        color: "#1A3A8F",
                        fontSize: 15,
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                >
                    <span aria-hidden="true">✕</span>
                </button>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    }}
                >
                    {/* ------------------ left: the hoisting scene ------------------ */}
                    <div
                        aria-hidden="true"
                        style={{
                            position: "relative",
                            overflow: "hidden",
                            minHeight: "var(--id-scene-h, 496px)",
                            borderRight: "1px solid #F0F2F0",
                            background:
                                "linear-gradient(180deg, #FFE9D2 0%, #FFF6EC 34%, #FFFFFF 62%, #F1F9EF 100%)",
                        }}
                    >
                        {/* sun glow */}
                        <span
                            style={{
                                position: "absolute",
                                top: 54,
                                left: "50%",
                                marginLeft: -130,
                                width: 260,
                                height: 260,
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(255,119,34,.18), rgba(255,119,34,.05) 52%, transparent 74%)",
                                animation:
                                    "id-scrim 1.4s ease .2s both, id-glow 8s ease-in-out 1.4s infinite",
                            }}
                        />

                        {/* Stars. Placement lives on the outer <g>, the animation on the
                          * inner one: put both on a single element and the keyframe's
                          * transform replaces the translate, collapsing every star onto
                          * the SVG origin and out of frame. */}
                        <svg
                            viewBox="0 0 300 480"
                            preserveAspectRatio="xMidYMid slice"
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 2,
                                pointerEvents: "none",
                                width: "100%",
                                height: "100%",
                            }}
                        >
                            <g>
                                {STARS.map(([x, y, s, dur, delay]) => (
                                    <g key={`${x}-${y}`} transform={`translate(${x} ${y}) scale(${s})`}>
                                        <g
                                            style={{
                                                transformOrigin: "0 0",
                                                /* NEGATIVE delay: every star is already mid-cycle
                                                 * on frame 1, so the sky sparkles from the moment
                                                 * the card opens and never pauses. */
                                                animation: `id-twinkle ${dur}s ease-in-out -${delay}s infinite`,
                                            }}
                                        >
                                            {/* the lower sky is near-white, so stars below y=230
                                              * take the deeper gold or they disappear into it */}
                                            <path d={PETAL} fill={y > 230 ? "#D9902B" : "#E3A21F"} />
                                            <path d={PETAL} fill="#FFF3E0" transform="rotate(45) scale(.5)" />
                                        </g>
                                    </g>
                                ))}
                            </g>
                        </svg>

                        {/* ground, horizon line, base shadow */}
                        <span
                            style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: "var(--id-ground, 84px)",
                                background: "linear-gradient(180deg, #F4F9F2, #E4EFE1)",
                            }}
                        />
                        <span
                            style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: "var(--id-ground, 84px)",
                                height: 1,
                                background: "rgba(26,58,143,.1)",
                            }}
                        />
                        <span
                            style={{
                                position: "absolute",
                                left: "50%",
                                bottom: "calc(var(--id-ground, 84px) - 10px)",
                                marginLeft: -62,
                                width: 124,
                                height: 18,
                                borderRadius: "50%",
                                background: "rgba(26,58,143,.1)",
                                filter: "blur(5px)",
                            }}
                        />

                        {/* The rig: pole, plate, finial and the flag, as one piece.
                          *
                          * Everything inside is measured from the GROUND LINE, which is
                          * this element's own bottom edge, and the whole thing scales by
                          * --id-scale. That is what makes the scene responsive without
                          * touching a single one of the interlocked numbers below — the
                          * 356px pole, the 264px climb in id-hoist, the 120x80 flag and
                          * the flower coordinates all stay exactly as they are, and the
                          * phone simply gets a smaller copy of the same scene. */}
                        <div
                            style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: "var(--id-ground, 84px)",
                                height: 0,
                                transform: "scale(var(--id-scale, 1))",
                                transformOrigin: "bottom center",
                            }}
                        >
                            <span
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: 0,
                                    marginLeft: -2.5,
                                    width: 5,
                                    height: 356,
                                    borderRadius: 3,
                                    background: "linear-gradient(180deg, #A9B3C7, #DCE1EA)",
                                    transformOrigin: "bottom center",
                                    animation: "id-pole 1.1s cubic-bezier(.2,.8,.2,1) .2s both",
                                }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: -6,
                                    marginLeft: -30,
                                    width: 60,
                                    height: 13,
                                    borderRadius: 7,
                                    background: "#DCE1EA",
                                }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: 352,
                                    marginLeft: -6,
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    background: "linear-gradient(150deg, #F5C86B, #E3A21F)",
                                    animation: "id-scrim .6s ease 1.15s both",
                                }}
                            />

                        {/* Hoist wrapper. This element only translates, which is why the
                          * flowers hang off it rather than off the flag — the flag is
                          * being scaleX-ed by the unfurl and would squash them. */}
                        <div
                            style={{
                                position: "absolute",
                                left: "50%",
                                bottom: 12,
                                marginLeft: 2,
                                animation: "id-hoist 2.6s cubic-bezier(.4,.55,.25,1) 1.2s both",
                            }}
                        >
                            <div
                                style={{
                                    position: "relative",
                                    width: 120,
                                    height: 80,
                                    transformOrigin: "left center",
                                    animation:
                                        "id-unfurl-x .85s cubic-bezier(.2,.8,.2,1) 3.6s both, id-wind 3.8s ease-in-out 4.5s infinite",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        overflow: "hidden",
                                        borderRadius: 3,
                                        boxShadow: "0 12px 26px rgba(26,58,143,.2)",
                                    }}
                                >
                                    <FlagSvg />
                                </div>
                                <span
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        borderRadius: 3,
                                        background:
                                            "linear-gradient(100deg, rgba(255,255,255,0) 34%, rgba(255,255,255,.5) 47%, rgba(255,255,255,0) 60%)",
                                        backgroundSize: "260% 100%",
                                        animation: "id-ripple 3.8s linear 4.5s infinite",
                                    }}
                                />
                            </div>

                            {/* the shower — a sibling of the flag, never a child of it */}
                            <div
                                style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    width: 120,
                                    height: 80,
                                    pointerEvents: "none",
                                }}
                            >
                                {FLOWERS.map((f, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            position: "absolute",
                                            left: f.left,
                                            top: f.top,
                                            display: "block",
                                            opacity: 0,
                                            ...(f.leaf
                                                ? {
                                                      width: 8,
                                                      height: 5,
                                                      borderRadius: "60% 10% 60% 10%",
                                                      background: f.petal,
                                                  }
                                                : null),
                                            animation: `id-shower-${f.anim} ${f.dur}s cubic-bezier(.35,.15,.5,1) ${f.delay}s both`,
                                        }}
                                    >
                                        {!f.leaf && (
                                            <Marigold
                                                size={f.size}
                                                petals={f.petals}
                                                petal={f.petal}
                                                centre={f.centre}
                                            />
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                        </div>
                    </div>

                    {/* ---------------------- right: the wish ---------------------- */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            gap: "var(--id-wish-gap, 16px)",
                            padding: "var(--id-wish-pad, 34px 40px 32px)",
                        }}
                    >
                        <p
                            style={{
                                margin: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "7px 16px",
                                borderRadius: 999,
                                background: "#FFF4EC",
                                border: "1px solid rgba(255,119,34,.3)",
                                fontSize: 11.5,
                                fontWeight: 600,
                                letterSpacing: ".12em",
                                textTransform: "uppercase",
                                color: "#1A3A8F",
                                animation: "id-rise .6s ease .62s both",
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "#138808",
                                    flex: "none",
                                }}
                            />
                            15 August · 80th Independence Day
                        </p>

                        <h2
                            style={{
                                margin: 0,
                                fontSize: "var(--id-title-size, clamp(26px, 3.2vw, 34px))",
                                lineHeight: 1.16,
                                fontWeight: 800,
                                letterSpacing: "-.02em",
                                backgroundImage:
                                    "linear-gradient(100deg, #FF7722 0%, #FF7722 32%, #1A3A8F 50%, #138808 68%, #138808 100%)",
                                backgroundSize: "220% 100%",
                                WebkitBackgroundClip: "text",
                                backgroundClip: "text",
                                color: "transparent",
                                WebkitTextFillColor: "transparent",
                                animation:
                                    "id-rise .7s ease .74s both, id-shine 6s ease-in-out 1.4s infinite",
                            }}
                        >
                            Happy Independence Day
                        </h2>

                        <p
                            style={{
                                margin: 0,
                                maxWidth: 420,
                                fontSize: "var(--id-copy-size, 15.5px)",
                                lineHeight: 1.68,
                                color: "#3E4A5C",
                                animation: "id-rise .7s ease .86s both",
                            }}
                        >
                            Every journey on our roads is a journey in freedom. On this 80th
                            Independence Day, WAY4TRACK wishes you and your fleet safe miles, open
                            highways, and a proud tricolour overhead.
                        </p>

                        <p
                            style={{
                                margin: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: ".06em",
                                textTransform: "uppercase",
                                color: "#138808",
                                animation: "id-rise .7s ease .98s both",
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{ width: 26, height: 2, background: "#FF7722", flex: "none" }}
                            />
                            Freedom to move. Safety for all.
                            <span
                                aria-hidden="true"
                                style={{ width: 26, height: 2, background: "#138808", flex: "none" }}
                            />
                        </p>

                        <button
                            type="button"
                            onClick={dismiss}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            style={{
                                padding: "var(--id-btn-pad, 15px 32px)",
                                border: "none",
                                borderRadius: 11,
                                cursor: "pointer",
                                color: "#fff",
                                fontSize: "var(--id-btn-size, 15.5px)",
                                fontWeight: 600,
                                background: hover
                                    ? "linear-gradient(135deg, #2F9E3F, #138808)"
                                    : "linear-gradient(135deg, #FF9147, #FF7722)",
                                boxShadow: hover
                                    ? "0 14px 30px rgba(19,136,8,.3)"
                                    : "0 12px 26px rgba(255,119,34,.32)",
                                transition: "background .25s ease, box-shadow .25s ease",
                                animation: "id-rise .7s ease 1.1s both",
                            }}
                        >
                            Enter WAY4TRACK <span aria-hidden="true">→</span>
                        </button>

                        <p
                            style={{
                                margin: 0,
                                fontSize: 12,
                                color: "#8A93A5",
                                letterSpacing: ".04em",
                                animation: "id-rise .7s ease 1.2s both",
                            }}
                        >
                            Team WAY4TRACK · Track anything, anytime, anywhere
                        </p>
                    </div>
                </div>

                <Strip />
            </div>
        </div>,
        document.body
    );
}
